import React from "react";
import SeoLanding from "./SeoLanding";

export default function ApprentissageDuJaponais() {
  return (
    <SeoLanding
      testid="seo-apprentissage"
      title="Apprentissage du japonais : Méthode complète en 90 jours | Japonais Pas à Pas"
      description="Apprentissage du japonais : méthode structurée en 90 jours pour francophones. Hiragana, katakana, kanji, grammaire, JLPT N5 à N3. Tuteur IA, audio natif. Essai 3 jours gratuit."
      keywords="apprentissage du japonais, méthode apprentissage japonais, apprentissage langue japonaise, apprentissage japonais débutant, progression japonais, parcours japonais JLPT, étapes apprentissage japonais"
      canonical="https://japonais-pas-a-pas.com/apprentissage-du-japonais"
      h1="Apprentissage du japonais : la méthode complète en 90 jours"
      subtitle="Un parcours structuré, des paliers concrets, et de la joie à chaque leçon. Du JLPT N5 au N3."
      lead="L'apprentissage du japonais demande une méthode claire et un cadre rassurant. Sans cela, 80% des débutants abandonnent en moins de 3 mois. Japonais Pas à Pas propose un parcours d'apprentissage du japonais structuré en 5 paliers, avec des objectifs concrets à chaque étape. Notre méthode est pensée par une équipe franco-japonaise pour les francophones."
      sections={[
        {
          h2: "Les 5 paliers de l'apprentissage du japonais",
          paragraphs: [
            "Notre méthode d'<strong>apprentissage du japonais</strong> s'articule autour de 5 paliers progressifs. Chacun valide un acquis concret, mesurable, motivant. Plus de zone floue, plus de « je ne sais pas si j'avance ».",
          ],
          bullets: [
            "<strong>Palier 1 (semaine 1)</strong> : hiragana maîtrisés, tu lis n'importe quel texte écrit en hiragana",
            "<strong>Palier 2 (semaine 2)</strong> : katakana maîtrisés, tu lis les menus de restaurants, les marques",
            "<strong>Palier 3 (mois 1-3)</strong> : JLPT N5, tu te présentes, comprends 100 kanji, tiens une conversation simple",
            "<strong>Palier 4 (mois 4-12)</strong> : JLPT N4, tu lis du manga shōnen, comprends des dialogues quotidiens",
            "<strong>Palier 5 (année 2)</strong> : JLPT N3, tu lis des articles, regardes des films sous-titrés sans bloquer",
          ],
        },
        {
          h2: "L'apprentissage du japonais : les 4 piliers de notre pédagogie",
          paragraphs: [
            "Notre approche de l'<strong>apprentissage du japonais</strong> repose sur quatre piliers :",
          ],
          bullets: [
            "<strong>1. La micro-pédagogie</strong> : des leçons de 15 minutes maximum, calibrées pour la concentration moderne. Pas de marathon, pas de fatigue cognitive",
            "<strong>2. La répétition espacée (SRS)</strong> : notre système te propose à réviser uniquement les éléments que ton cerveau s'apprête à oublier. Efficacité maximale, effort minimal",
            "<strong>3. L'immersion auditive native</strong> : Atsuko et Alexis, deux voix authentiquement japonaises, prononcent chaque syllabe. Ton oreille s'habitue dès le jour 1",
            "<strong>4. L'accompagnement IA</strong> : un tuteur IA en français répond à toutes tes questions, même à 23h. C'est comme avoir un professeur particulier dans ta poche, sans le coût",
          ],
        },
        {
          h2: "Les erreurs classiques dans l'apprentissage du japonais (à éviter)",
          paragraphs: [
            "Voici les pièges les plus fréquents pour les francophones qui démarrent l'<strong>apprentissage du japonais</strong> :",
          ],
          bullets: [
            "<strong>Vouloir apprendre les kanji en premier</strong> : non. Commence par hiragana → katakana → grammaire de base → kanji. Sinon, tu te noies",
            "<strong>S'appuyer sur le romaji trop longtemps</strong> : c'est une béquille qui empêche ton cerveau d'enregistrer les sons japonais. Passe aux kana dès la semaine 1",
            "<strong>Vouloir parler avant d'écouter</strong> : ton accent se formera mal si tu n'as pas internalisé les sons. Écoute, écoute, écoute, puis répète",
            "<strong>Sauter les particules (は wa, を o, に ni)</strong> : elles sont la colonne vertébrale du japonais. Notre tuteur IA t'aide à les comprendre une bonne fois pour toutes",
            "<strong>Faire des sessions marathon</strong> : 2h le dimanche, puis rien du lundi au vendredi = échec garanti. Préfère 15 min par jour, sans exception",
          ],
        },
        {
          h2: "Apprentissage du japonais : les outils que nous mettons à ta disposition",
          paragraphs: [
            "Pour structurer ton <strong>apprentissage du japonais</strong>, tu disposes de :",
          ],
          bullets: [
            "<strong>200 leçons interactives</strong> avec quiz, audio natif et corrections instantanées",
            "<strong>Tableau hiragana et katakana</strong> interactif avec audio sur chaque caractère",
            "<strong>636 mots de vocabulaire</strong> classés par thème (voyage, business, famille, restaurant…)",
            "<strong>Système de révision intelligente</strong> qui s'adapte à ta mémoire",
            "<strong>Examens blancs JLPT</strong> chronométrés (N5 gratuit, N4 et N3 en premium)",
            "<strong>Tuteur IA Sensei</strong> qui répond en français à toutes tes questions",
            "<strong>PDF gratuit</strong> : 50 phrases japonaises pour voyager au Japon",
          ],
        },
      ]}
      faq={[
        {
          q: "Quel niveau atteint-on après 3 mois d'apprentissage du japonais ?",
          a: "Avec 15 minutes par jour, tu atteins le niveau JLPT N5 : présenter-toi, comprendre des dialogues simples, lire ~100 kanji, écrire en hiragana et katakana. C'est la fondation solide qui permet tout le reste.",
        },
        {
          q: "L'apprentissage du japonais peut-il se faire en autodidacte ?",
          a: "Oui, mais avec une bonne plateforme. L'autodidaxie pure (livres + Internet désordonné) mène à l'abandon dans 80% des cas. Notre méthode structurée + le tuteur IA reproduisent l'effet d'un prof, à un coût accessible.",
        },
        {
          q: "Quels sont les bons réflexes pour ne pas abandonner ?",
          a: "1) Une routine fixe (toujours à la même heure), 2) Un rythme soutenable (15 min/jour > 2h le dimanche), 3) Des objectifs concrets (pas « apprendre le japonais », mais « réussir le JLPT N5 en mars »), 4) Célébrer chaque palier validé.",
        },
        {
          q: "Combien coûte un parcours d'apprentissage complet ?",
          a: "L'essai gratuit dure 3 jours sans carte bancaire. Ensuite : 12,90 €/mois, 89 €/an (43% off) ou 199 € à vie. Notre offre Fondateur à 49 € à vie est limitée aux 100 premiers étudiants.",
        },
        {
          q: "L'apprentissage du japonais est-il adapté aux enfants/adolescents ?",
          a: "Oui pour les ados (12+). Le format ludique, les leçons courtes et le tuteur IA fonctionnent bien à partir de cet âge. Pour les plus jeunes, il faut un parent à côté pour expliquer certaines tournures.",
        },
      ]}
    />
  );
}
